import React, { useState, useEffect, useRef } from 'react';

import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";

import {
  Grid,
  Box,
  CircularProgress,
  Tooltip,
  Typography,
  ClickAwayListener,
  Popper,
  IconButton,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import ModalContainer from 'components/ModalContainer';
import GlobalAlert from "components/Notification";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { MenuSelect } from "components/Select/menuSelect";
import AutocompleteSelect from "components/Select/AutocompleteSelect";
import InputField from "components/InputField/index.js";
import selectStyle from "components/Select/mainStyle.module.scss";

import ModalforCheck from '../modalCheck.js';
import ModalForAddOrgOrSite from '../ModalForAddOrgOrSite.js';

import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show.svg";
import infoTipIcon from "assets/images/icons/icn_Info_solid.svg";
import infoTipHoverIcon from "assets/images/icons/icn_info_solid_hover.svg";
import { ReactComponent as NtpRemoveIcon } from "assets/images/icons/icn_table_cell_Remove.svg";
import { formatTimestampToMinute } from "utils/format";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";
import { getCountryList, getTimezoneList } from "utils/timezone";
import { validateDeviceAccount, validateDeviceAccountPassword } from "utils/validate";

const passwordRequirementsTip = (
  <Box sx={{ fontSize: "14px", lineHeight: 1.5 }}>
    <Typography sx={{ fontSize: "14px", fontWeight: 700, mb: 0.5 }}>
      Password Requirements
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      <li>8–30 characters</li>
      <li>At least 1 uppercase letter (A–Z)</li>
      <li>At least 1 lowercase letter (a–z)</li>
      <li>At least 1 number (0–9)</li>
      <li>At least 1 special character (~ ! @ # $ ^ &amp; * &#123; &#125; [ ] ( ) _ - + = . | : ; &apos; &quot; &lt; , &gt; / \)</li>
      <li>No consecutive identical characters (e.g., 11, aa, 22)</li>
    </Box>
  </Box>
);

const normalizeDeviceStatus = (devices, fallbackOnline = 0) => ({
  online: devices?.online ?? fallbackOnline,
  offline: devices?.offline ?? 0,
  onboarding: devices?.onboarding ?? 0,
});

const normalizeClientCounts = (clients) => ({
  wireless: clients?.wireless ?? (typeof clients === "number" ? clients : 0),
  wired: clients?.wired ?? 0,
});

const normalizeAlerts = (alerts) => ({
  critical: alerts?.critical ?? 0,
  normal: alerts?.normal ?? 0,
  low: alerts?.low ?? 0,
});

const formatCreatedTime = (createdAt) => {
  return formatTimestampToMinute(createdAt);
};

const SupportBadge = ({ badge }) => {
  const displayName = badge?.currentSupportDisplayName;

  if (!displayName) return null;

  return (
    <Tooltip title={displayName} arrow placement="top">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          flexShrink: 1,
          minWidth: 0,
          maxWidth: "min(220px, 50%)",
          overflow: "hidden",
          height: "18px",
          ml: "7px",
          px: "7px",
          borderRadius: "9px",
          backgroundColor: "#DDF8DF",
          color: "#008A20",
          fontSize: "12px",
          fontWeight: 500,
          lineHeight: "18px",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: "13px",
            height: "13px",
            mr: "4px",
            borderRadius: "50%",
            backgroundColor: "#D9EDFF",
          }}
        >
          <ShieldOutlinedIcon sx={{ width: "10px", height: "10px", color: "#3B9BE7" }} />
        </Box>
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {`Support · ${displayName}`}
        </Box>
      </Box>
    </Tooltip>
  );
};

const OrgCloneTextField = (props) => <InputField {...props} />;

const Organization = () => {
  const { scopeData, setScopeData, scopeReady } = useOutletContext();
  const location = useLocation();
  const isSurveillance =
    localStorage.getItem("selectedSystem") === "surveillance" ||
    location.pathname.startsWith("/surveillance");

  const { bread, setBread } = useBreadStorageContext();

  const [manageOrgsUuid, _setManageOrgsUuid] = useState(uuidv4());
  const [nowUuid, setNowUuid] = useState('');

  const { dataMap, sendRequest } = useDataSourceManager();
  const [supportBadges, setSupportBadges] = useState({});
  const [supportBadgeRequestMap, setSupportBadgeRequestMap] = useState({});
  const requestedSupportBadgesRef = useRef(new Set());
  const processedSupportBadgesRef = useRef(new Set());

  const [openModalForAddOrg, setOpenModalForAddOrg] = useState(false);
  const [openAddOrgAction, setOpenAddOrgAction] = useState(false);
  const [openCloneOrgSource, setOpenCloneOrgSource] = useState(false);
  const [cloneOrgPage, setCloneOrgPage] = useState("source");
  const [expandedCloneOrgSites, setExpandedCloneOrgSites] = useState({});
  const [showCloneOrgPassword, setShowCloneOrgPassword] = useState(false);
  const [cloneSourceOrgId, setCloneSourceOrgId] = useState("");
  const [clonePlanRequestId, setClonePlanRequestId] = useState("");
  const [clonePlan, setClonePlan] = useState(null);
  const [clonePlanLoading, setClonePlanLoading] = useState(false);
  const [clonePlanError, setClonePlanError] = useState("");
  const [clonePlanAdvanceOnSuccess, setClonePlanAdvanceOnSuccess] = useState(false);
  const cloneUserManagementTree = true;
  const [cloneOrgRequestId, setCloneOrgRequestId] = useState("");
  const [cloneOrgLoading, setCloneOrgLoading] = useState(false);
  const [cloneOrgResult, setCloneOrgResult] = useState(null);
  const [cloneOrgAlert, setCloneOrgAlert] = useState({ open: false, severity: "error", message: "" });
  const [cloneOrgNeedsVerification, setCloneOrgNeedsVerification] = useState(false);
  const [cloneOrgVerificationRequestId, setCloneOrgVerificationRequestId] = useState("");
  const [cloneOrgVerificationFailed, setCloneOrgVerificationFailed] = useState(false);
  const cloneOrgStartedAtRef = useRef(0);
  const cloneOrgSuccessTimerRef = useRef(null);
  const cloneOrgRequestTimeoutRef = useRef(null);
  const [cloneOrgForm, setCloneOrgForm] = useState({
    name: "",
    countryIndex: "",
    timeZone: "",
    ntpServers: ["ntp1.nuclias.com"],
    username: "",
    password: "",
  });
  const [cloneOrgUsernameError, setCloneOrgUsernameError] = useState("");
  const [cloneOrgPasswordError, setCloneOrgPasswordError] = useState("");
  const clonePlanSourceRef = useRef("");
  const addOrgButtonRef = useRef(null);
  const [modalForAddOrgTitle, setModalForAddOrgTitle] = useState('');
  const [modalForAddOrgCancelBtn, setModalForAddOrgCancelBtn] = useState('');
  const [modalForAddOrgSaveBtn, setModalForAddOrgSaveBtn] = useState('');

  const [openModalCheck, setOpenModalCheck] = useState(false);
  const [modalCheckTitle, setModalCheckTitle] = useState('');
  const [modalCheckContent, setModalCheckContent] = useState('');
  const [modalCheckCancelBtn, setModalCheckCancelBtn] = useState('');
  const [modalCheckSaveBtn, setModalCheckSaveBtn] = useState('');

  const [modalFrom, setModalFrom] = useState('');
  const [enableAction, setEnableAction] = useState(false);

  const getDatas = async () => {
    setNowUuid(manageOrgsUuid)

    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    let tmpResource = '/ents/:entId/orgs.read';
    let tmpResourceAPIval = { entId: tmpSelectedBread.realm?.id }
    sendRequest(tmpResource, manageOrgsUuid, tmpResourceAPIval);
  };

  const verifyCloneOutcome = () => {
    const selectedBread = JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
    const requestId = uuidv4();
    setCloneOrgNeedsVerification(true);
    setCloneOrgVerificationFailed(false);
    setCloneOrgVerificationRequestId(requestId);
    sendRequest(
      "/ents/:entId/orgs.read",
      requestId,
      { entId: selectedBread.realm?.id },
    );
  };

  const mergeData = (orgList) => {
    let resultAry = []

    let manageOrgList = []
    if (orgList?.data?.orgs?.length > 0) {
      manageOrgList = orgList.data.orgs
    }

    for (let childEnt of manageOrgList) {
      const adoptedDevices = childEnt.adoptedDeviceNum ?? childEnt.adoptedDevices;
      let child = {
        id: childEnt.orgId,
        name: childEnt.orgName || childEnt.organizationName || childEnt.name || "-",
        roleId: childEnt.orgRoleId,
        roleName: childEnt.orgRoleName,
        siteCount: childEnt.siteNum ?? childEnt.siteCount ?? 0,
        adoptedDeviceCounts: {
          accessPoints: adoptedDevices?.ap ?? adoptedDevices?.accessPoints ?? 0,
          switches: adoptedDevices?.switch ?? adoptedDevices?.switches ?? 0,
          routers: adoptedDevices?.router ?? adoptedDevices?.routers ?? 0,
        },
        ...(isSurveillance
          ? {
            cameras: normalizeDeviceStatus(
              childEnt.cameraNum ?? childEnt.ipcamNum ?? childEnt.cameras,
              adoptedDevices?.camera ?? 0,
            ),
            nvrs: normalizeDeviceStatus(
              childEnt.nvrNum ?? childEnt.nvrs,
              adoptedDevices?.nvr ?? 0,
            ),
          }
          : {
            accessPoints: normalizeDeviceStatus(
              childEnt.accessPointNum ?? childEnt.apNum ?? childEnt.accessPoints,
              adoptedDevices?.accessPoints ?? 0,
            ),
            switches: normalizeDeviceStatus(
              childEnt.switchNum ?? childEnt.switches,
              adoptedDevices?.switches ?? 0,
            ),
          }),
        clients: normalizeClientCounts(childEnt.clientNum ?? childEnt.clients),
        alerts: normalizeAlerts(childEnt.alertNum ?? childEnt.alerts),
        createdTime: formatCreatedTime(childEnt.createdAt),
      }
      resultAry.push(child)
    }

    return resultAry;
  }

  const handleAddItem = () => {
    if (!enableAction) return;
    setOpenAddOrgAction(false);
    setModalFrom('Add')
    setModalForAddOrgTitle('Add Organization')
    setModalForAddOrgCancelBtn('Cancel')
    setModalForAddOrgSaveBtn('Save')
    setOpenModalForAddOrg(true)
  }

  const handleOpenCloneOrganization = () => {
    if (!enableAction) return;
    if (cloneOrgNeedsVerification) {
      setOpenAddOrgAction(false);
      setCloneOrgPage("summary");
      setOpenCloneOrgSource(true);
      verifyCloneOutcome();
      return;
    }
    clearTimeout(cloneOrgRequestTimeoutRef.current);
    cloneOrgRequestTimeoutRef.current = null;
    clearTimeout(cloneOrgSuccessTimerRef.current);
    cloneOrgSuccessTimerRef.current = null;
    setOpenAddOrgAction(false);
    setCloneOrgRequestId("");
    setCloneOrgLoading(false);
    setCloneOrgResult(null);
    setCloneOrgAlert({ open: false, severity: "error", message: "" });
    setCloneSourceOrgId("");
    setClonePlan(null);
    setClonePlanError("");
    setClonePlanAdvanceOnSuccess(false);
    setClonePlanLoading(false);
    setCloneOrgPage("source");
    setCloneOrgForm({ name: "", countryIndex: "", timeZone: "", ntpServers: ["ntp1.nuclias.com"], username: "", password: "" });
    setCloneOrgUsernameError("");
    setCloneOrgPasswordError("");
    setOpenCloneOrgSource(true);
  };

  const handleCloneSourceChange = (sourceOrgId) => {
    const sourceChanged = String(sourceOrgId) !== String(cloneSourceOrgId);
    setCloneSourceOrgId(sourceOrgId);
    setClonePlan(null);
    setClonePlanError("");
    setClonePlanLoading(false);
    setClonePlanRequestId("");
    clonePlanSourceRef.current = sourceOrgId;
    setClonePlanAdvanceOnSuccess(false);
    if (sourceChanged) {
      setCloneOrgForm({ name: "", countryIndex: "", timeZone: "", ntpServers: [""], username: "", password: "" });
      if (cloneOrgPage === "edit" && sourceOrgId) {
        requestClonePlan(sourceOrgId, false);
      }
    }
    if (!sourceOrgId) {
      setCloneOrgForm({ name: "", countryIndex: "", timeZone: "", ntpServers: [""], username: "", password: "" });
      return;
    }
  };

  const requestClonePlan = (sourceOrgId, advanceOnSuccess = false) => {
    if (!sourceOrgId || clonePlanLoading) return;
    const requestId = uuidv4();
    const selectedBread = JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
    setClonePlanRequestId(requestId);
    clonePlanSourceRef.current = sourceOrgId;
    setClonePlanLoading(true);
    setClonePlanError("");
    setClonePlanAdvanceOnSuccess(advanceOnSuccess);
    sendRequest(
      "/ents/:entId/orgs/:sourceOrgId/clonePlan.read",
      requestId,
      { entId: selectedBread.realm?.id, sourceOrgId },
      { cloneUserManagementTree },
    );
  };

  const loadClonePlanFromSource = () => {
    if (!cloneSourceOrgId || clonePlanLoading) return;
    if (clonePlan && !clonePlanError) {
      setCloneOrgPage("edit");
      return;
    }
    requestClonePlan(cloneSourceOrgId, true);
  };

  useEffect(() => {
    if (!clonePlanRequestId) return;
    const response = dataMap[clonePlanRequestId];
    if (!response) return;
    if (response.code || response.error) {
      setClonePlanLoading(false);
      setClonePlanError(response.error?.message || response.message || "Failed to load clone plan");
      setClonePlanAdvanceOnSuccess(false);
      return;
    }
    if (!response.data) return;

    if (String(clonePlanSourceRef.current) === String(cloneSourceOrgId)) {
      setClonePlan(response.data);
      setClonePlanError("");
      setClonePlanLoading(false);
      if (clonePlanAdvanceOnSuccess) {
        setCloneOrgPage("edit");
        setClonePlanAdvanceOnSuccess(false);
      }
      const defaults = response.data.defaults || {};
      const countryIndex = String(defaults.countryDomain?.countryIndex ?? "");
      const timeConfig = defaults.timeConfig || {};
      setCloneOrgForm({
        name: defaults.suggestedOrgName || "",
        countryIndex,
        timeZone: timeConfig.timezone || "",
        ntpServers: [timeConfig.ntpServer1 || ""],
        username: defaults.deviceAccount?.username || "",
        password: defaults.deviceAccount?.password || "",
      });
      setCloneOrgUsernameError("");
      setCloneOrgPasswordError("");
    }
  }, [dataMap, clonePlanRequestId, cloneSourceOrgId, clonePlanAdvanceOnSuccess]);

  useEffect(() => {
    if (!cloneOrgRequestId) return;
    const response = dataMap[cloneOrgRequestId];
    if (!response) return;
    clearTimeout(cloneOrgRequestTimeoutRef.current);
    cloneOrgRequestTimeoutRef.current = null;

    const errorMessage = response.error?.message || response.message || response.error;
    const hasError = Boolean(response.error || response.code);
    if (hasError) {
      setCloneOrgLoading(false);
      const errorCode = Number(response.code || response.error?.code);
      const outcomeUnknown = errorCode === 500 || String(errorMessage).toLowerCase().includes("timeout");
      setCloneOrgPage("summary");
      if (outcomeUnknown) verifyCloneOutcome();
      setCloneOrgAlert({
        open: true,
        severity: "error",
        message: outcomeUnknown
          ? `${typeof errorMessage === "string" ? errorMessage : "The result is unknown."} Checking the MSP Organization list before allowing a retry.`
          : typeof errorMessage === "string"
            ? errorMessage
            : "Organization clone failed. Please try again.",
      });
      setCloneOrgRequestId("");
      return;
    }

    const result = response.data || response.result?.data || response.result || {};
    if (!result.orgId) {
      setCloneOrgLoading(false);
      setCloneOrgPage("summary");
      setCloneOrgAlert({
        open: true,
        severity: "error",
        message: "The clone request completed without an Organization ID. Please try again.",
      });
      setCloneOrgRequestId("");
      return;
    }

    setCloneOrgResult(result);
    setCloneOrgRequestId("");
    const remainingLoadingMs = Math.max(
      0,
      1500 - (Date.now() - cloneOrgStartedAtRef.current),
    );
    cloneOrgSuccessTimerRef.current = setTimeout(() => {
      setCloneOrgLoading(false);
      setCloneOrgPage("success");
      getDatas();
    }, remainingLoadingMs);
  }, [dataMap, cloneOrgRequestId]);

  useEffect(() => {
    if (!cloneOrgVerificationRequestId) return;
    const response = dataMap[cloneOrgVerificationRequestId];
    if (!response) return;
    setCloneOrgVerificationRequestId("");
    if (response.code || response.error) {
      setCloneOrgVerificationFailed(true);
      setCloneOrgAlert({
        open: true,
        severity: "error",
        message: "Could not verify the Organization list. Retry the list check before submitting the clone again.",
      });
      return;
    }
    const organizations = response.data?.orgs || [];
    const matchingOrg = organizations.find((organization) =>
      String(organization.orgName || organization.name || "").trim() === cloneOrgForm.name.trim(),
    );
    if (matchingOrg?.orgId) {
      setCloneOrgResult({
        orgId: matchingOrg.orgId,
        orgName: matchingOrg.orgName || matchingOrg.name,
        siteCount: matchingOrg.siteNum ?? matchingOrg.siteCount ?? 0,
      });
      setCloneOrgNeedsVerification(false);
      setCloneOrgLoading(false);
      setCloneOrgPage("success");
      getDatas();
      return;
    }
    setCloneOrgNeedsVerification(false);
    setCloneOrgVerificationFailed(false);
    setCloneOrgAlert({
      open: true,
      severity: "error",
      message: "The MSP Organization list was checked and the new Organization was not found. You may retry the clone.",
    });
  }, [dataMap, cloneOrgVerificationRequestId, cloneOrgForm.name]);

  const renderAddOrganizationMenu = () => (
    <Box>
      <Box ref={addOrgButtonRef} sx={{ display: "inline-flex" }}>
        <SolidButton
          disabled={!enableAction}
          startIcon={<AddIcon />}
          height="32px"
          onClick={() => setOpenAddOrgAction((open) => !open)}
        >
          Add Organization
        </SolidButton>
      </Box>
      <Popper
        open={openAddOrgAction}
        anchorEl={addOrgButtonRef.current}
        placement="bottom-end"
        strategy="fixed"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpenAddOrgAction(false)}>
          <Box
            sx={{
              position: "relative",
              width: "max-content",
              boxSizing: "border-box",
              bgcolor: "primary.lightBlue",
              px: "20px",
              py: "10px",
              borderRadius: 4,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "-8px",
                right: "20px",
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: "8px solid",
                borderBottomColor: "primary.lightBlue",
              },
            }}
          >
            {[
              ["Add New Organization", handleAddItem],
              ["Clone an Existing Organization", handleOpenCloneOrganization],
            ].map(([label, onClick], index) => (
              <Box
                key={label}
                role="menuitem"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") onClick();
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  height: "50px",
                  px: "10px",
                  boxSizing: "border-box",
                  color: "primary.main",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  ...(index === 0 && {
                    borderBottom: "1px solid",
                    borderColor: "primary.borderGreyPrimary",
                  }),
                  "&:hover": { color: "primary.mainHover" },
                  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
                }}
              >
                <span>{label}</span>
              </Box>
            ))}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );

  const handleScopeLink = (item) => {
    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');

    // save storage
    tmpSelectedBread.orgs = item;
    tmpSelectedBread.site = {};
    localStorage.setItem('selectBreadcrumbs', JSON.stringify(tmpSelectedBread));

    setBread(tmpSelectedBread)
    // navigate
    navigatePage(item);
  }

  const navigate = useNavigate();
  const navigatePage = () => {
    let path = "/org/sites";
    navigate(path);
  }

  const checkPermissions = () => {
    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    if (tmpSelectedBread && Object.keys(tmpSelectedBread).length > 0) {
      if (tmpSelectedBread.realm.roleId === 1 || tmpSelectedBread.realm.roleId === 2) {
        setEnableAction(true)
      } else {
        setEnableAction(false)
      }
    }
  }

  useEffect(() => {
    checkPermissions()
  }, [bread]);

  useEffect(() => {
    if (scopeReady) {
      checkPermissions()
    }
  }, [scopeReady]);

  function handleActionDenied(message) {
    setOpenModalForAddOrg(false);
    setModalCheckTitle('Action denied')
    setModalCheckContent(message)
    setModalCheckCancelBtn('')
    setModalCheckSaveBtn('OK')
    setModalFrom('error')
    setOpenModalCheck(true)
  }

  useEffect(() => {
    if (nowUuid !== '') {
      switch (nowUuid) {
        case manageOrgsUuid:
          if (dataMap[manageOrgsUuid]?.code !== undefined) {
            setModalCheckTitle('Action denied')
            setModalCheckContent(dataMap[manageOrgsUuid].message)
            setModalCheckCancelBtn('')
            setModalCheckSaveBtn('OK')
            setModalFrom('error')
            setOpenModalCheck(true)
          } else if (dataMap[manageOrgsUuid] !== undefined) {
            const result = mergeData(dataMap[manageOrgsUuid])
            setScopeData(prev => ({
              ...prev,
              orgs: result
            }));
          }

          setNowUuid('')
          break;
        default:
          break;
      }
    }
  }, [dataMap]);

  const columnHelper = createColumnHelper();
  const renderDeviceStatus = (devices) =>
    `${devices?.online ?? 0} | ${devices?.offline ?? 0} | ${devices?.onboarding ?? 0
    }`;
  const renderCountTooltip = (value, label) => (
    <Tooltip title={label} arrow placement="bottom">
      <Box component="span" sx={{ cursor: "default" }}>
        {value}
      </Box>
    </Tooltip>
  );

  const getAlertTotal = (alerts) =>
    (alerts?.critical ?? 0) + (alerts?.normal ?? 0) + (alerts?.low ?? 0);

  const networkColumns = [
    columnHelper.accessor("name", {
      header: "Organization",
      cell: (info) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
          <EllipsisTooltipCell
            text={info.getValue()}
            onClick={() => handleScopeLink(info.row.original)}
            sx={{
              color: "#707ca8",
              cursor: "pointer",
              textDecoration: "underline",
              flex: "0 1 40%",
              minWidth: 0,
              maxWidth: "40%",
            }}
          />
          {renderSupportBadges(info.row.original.id)}
        </Box>
      ),
    }),
    columnHelper.accessor("siteCount", { header: "Sites" }),
    columnHelper.accessor("adoptedDeviceCounts.accessPoints", {
      header: "APs",
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor("adoptedDeviceCounts.switches", {
      header: "Switches",
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor("adoptedDeviceCounts.routers", {
      header: "Gateways",
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor("alerts", {
      header: "Alerts",
      cell: (info) => getAlertTotal(info.getValue()),
    }),
    columnHelper.accessor("daysLeft", {
      header: "Days left",
      cell: (info) => Number(info.getValue()) || 0,
    }),
  ];

  const surveillanceColumns = [
    columnHelper.accessor("name", {
      header: "Name",
      cell: (info) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
          <EllipsisTooltipCell
            text={info.getValue()}
            onClick={() => handleScopeLink(info.row.original)}
            sx={{
              color: "#707ca8",
              cursor: "pointer",
              textDecoration: "underline",
              flex: "0 1 40%",
              minWidth: 0,
              maxWidth: "40%",
            }}
          />
          {renderSupportBadges(info.row.original.id)}
        </Box>
      ),
    }),
    columnHelper.accessor("roleName", { header: "Role" }),
    columnHelper.accessor("siteCount", { header: "Site" }),
    columnHelper.accessor("cameras", {
      header: "Camera",
      cell: (info) => renderCountTooltip(
        renderDeviceStatus(info.getValue()),
        "Online | Offline | Onboarding",
      ),
    }),
    columnHelper.accessor("nvrs", {
      header: "NVR",
      cell: (info) => renderCountTooltip(
        renderDeviceStatus(info.getValue()),
        "Online | Offline | Onboarding",
      ),
    }),
    columnHelper.accessor("createdTime", { header: "Created Time" }),
  ];

  const columnsData = isSurveillance ? surveillanceColumns : networkColumns;

  const orgData = Array.isArray(scopeData?.orgs) ? scopeData.orgs : [];
  const cloneOrgCountryOptions = getCountryList();
  const cloneOrgTimezoneOptions = cloneOrgForm.countryIndex
    ? getTimezoneList(Number(cloneOrgForm.countryIndex))
    : [];
  const cloneOrgFormValid = Boolean(
    cloneOrgForm.name.trim() &&
    cloneOrgForm.countryIndex &&
    cloneOrgTimezoneOptions.some((timezone) => timezone.value === cloneOrgForm.timeZone) &&
    cloneOrgForm.ntpServers.length > 0 &&
    cloneOrgForm.ntpServers.every((server) => server.trim()) &&
    validateDeviceAccount(cloneOrgForm.username).valid &&
    validateDeviceAccountPassword(cloneOrgForm.password, cloneOrgForm.username).valid,
  );
  const cloneOrgSourceName = orgData.find(
    (organization) => String(organization.id) === String(cloneSourceOrgId),
  )?.name || "";
  const cloneOrgCountryName = cloneOrgCountryOptions.find(
    (country) => String(country.value) === String(cloneOrgForm.countryIndex),
  )?.label || "";
  const cloneOrgTimezoneName = cloneOrgTimezoneOptions.find(
    (timezone) => timezone.value === cloneOrgForm.timeZone,
  )?.label || cloneOrgForm.timeZone;

  const handleCloneOrgFormChange = (key, value) => {
    setCloneOrgForm((previous) => ({ ...previous, [key]: value }));
    if (key === "username") {
      const result = validateDeviceAccount(value);
      setCloneOrgUsernameError(result.valid || !value ? "" : result.msg);
      setCloneOrgPasswordError(
        cloneOrgForm.password && !validateDeviceAccountPassword(cloneOrgForm.password, value).valid
          ? "Password does not meet the requirements."
          : "",
      );
    }
    if (key === "password") {
      const result = validateDeviceAccountPassword(value, cloneOrgForm.username);
      setCloneOrgPasswordError(result.valid || !value ? "" : result.msg);
    }
  };

  const handleCloneOrgCountryChange = (option) => {
    const countryIndex = String(option?.value || "");
    const timezones = countryIndex ? getTimezoneList(Number(countryIndex)) : [];
    setCloneOrgForm((previous) => ({
      ...previous,
      countryIndex,
      timeZone: timezones[0]?.value || "",
    }));
  };

  const handleCloneOrgNtpChange = (index, value) => {
    setCloneOrgForm((previous) => ({
      ...previous,
      ntpServers: previous.ntpServers.map((server, serverIndex) =>
        serverIndex === index ? value : server,
      ),
    }));
  };

  const handleCloneOrganization = () => {
    if (cloneOrgLoading || !clonePlan || !cloneOrgFormValid) return;

    const selectedBread = JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
    const requestId = uuidv4();
    cloneOrgStartedAtRef.current = Date.now();
    const payload = {
      data: {
        sourceOrgId: cloneSourceOrgId,
        cloneUserManagementTree,
        planRevision: clonePlan.planRevision,
        orgName: cloneOrgForm.name.trim(),
        countryDomain: { countryIndex: Number(cloneOrgForm.countryIndex) },
        timeConfig: {
          timezone: cloneOrgForm.timeZone,
          ntpServer1: cloneOrgForm.ntpServers[0] || "",
          ntpServer2: cloneOrgForm.ntpServers[1] || "",
          ntpServer3: cloneOrgForm.ntpServers[2] || "",
        },
        deviceAccount: {
          username: cloneOrgForm.username,
          password: cloneOrgForm.password,
        },
      },
    };

    setCloneOrgRequestId(requestId);
    setCloneOrgResult(null);
    setCloneOrgAlert({ open: false, severity: "error", message: "" });
    setCloneOrgLoading(true);
    setCloneOrgPage("cloning");
    setCloneOrgNeedsVerification(false);
    setCloneOrgVerificationFailed(false);
    sendRequest(
      "/ents/:entId/orgClones.create",
      requestId,
      { entId: selectedBread.realm?.id },
      payload,
    );
    cloneOrgRequestTimeoutRef.current = setTimeout(() => {
      setCloneOrgRequestId("");
      setCloneOrgLoading(false);
      setCloneOrgPage("summary");
      setCloneOrgAlert({
        open: true,
        severity: "error",
        message: "The clone request timed out. Checking the MSP Organization list before allowing a retry.",
      });
      verifyCloneOutcome();
    }, 5 * 60 * 1000);
  };

  const handleOpenClonedOrganization = (destination) => {
    if (!cloneOrgResult?.orgId) return;
    clearTimeout(cloneOrgRequestTimeoutRef.current);
    cloneOrgRequestTimeoutRef.current = null;
    const selectedBread = JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
    const sourceOrganization = orgData.find(
      (organization) => String(organization.id) === String(cloneSourceOrgId),
    );
    const newOrganization = {
      id: cloneOrgResult.orgId,
      orgId: cloneOrgResult.orgId,
      name: cloneOrgResult.orgName || cloneOrgForm.name,
      orgName: cloneOrgResult.orgName || cloneOrgForm.name,
      roleId: sourceOrganization?.roleId,
      roleName: sourceOrganization?.roleName,
    };
    selectedBread.orgs = newOrganization;
    selectedBread.site = {};
    localStorage.setItem("selectBreadcrumbs", JSON.stringify(selectedBread));
    setBread(selectedBread);
    setOpenCloneOrgSource(false);
    getDatas();
    navigate(destination === "devices" ? "/org/devices" : "/org/sites", {
      state: {
        orgId: cloneOrgResult.orgId,
        ...(destination === "devices" ? { openDiscover: true } : {}),
      },
    });
  };

  const handleCloseCloneOrgSuccess = () => {
    clearTimeout(cloneOrgRequestTimeoutRef.current);
    cloneOrgRequestTimeoutRef.current = null;
    setOpenCloneOrgSource(false);
    getDatas();
  };

  const addCloneOrgNtpServer = () => {
    setCloneOrgForm((previous) =>
      previous.ntpServers.length >= 3
        ? previous
        : { ...previous, ntpServers: [...previous.ntpServers, ""] },
    );
  };

  const removeCloneOrgNtpServer = (index) => {
    setCloneOrgForm((previous) => ({
      ...previous,
      ntpServers: previous.ntpServers.filter((_, serverIndex) => serverIndex !== index),
    }));
  };

  useEffect(() => {
    if (isSurveillance || orgData.length === 0) return;

    let supportSession = null;
    try {
      supportSession = JSON.parse(sessionStorage.getItem("supportSession") || "null");
    } catch (error) {
      console.error("[SupportBadge] Invalid supportSession:", error);
    }
    if (supportSession?.supportTokenId) return;

    let entId = "";
    try {
      const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
      entId = breadcrumbs?.realm?.id || "";
    } catch (error) {
      console.error("[SupportBadge] Failed to read selected enterprise:", error);
    }
    if (!entId) return;

    orgData.forEach((org) => {
      const orgId = org?.id;
      if (!orgId || requestedSupportBadgesRef.current.has(orgId)) return;

      requestedSupportBadgesRef.current.add(orgId);
      const requestUuid = `support-${uuidv4()}`;
      setSupportBadgeRequestMap((previous) => ({
        ...previous,
        [requestUuid]: orgId,
      }));
      sendRequest(
        "/ents/:entId/orgs/:orgId/supportTokens/badges.read",
        requestUuid,
        { entId, orgId },
      );
    });
  }, [orgData, isSurveillance, sendRequest]);

  useEffect(() => {
    Object.entries(supportBadgeRequestMap).forEach(([requestUuid, orgId]) => {
      if (processedSupportBadgesRef.current.has(requestUuid)) return;
      const response = dataMap[requestUuid];
      if (!response) return;

      processedSupportBadgesRef.current.add(requestUuid);
      const items =
        response?.result?.data?.items ||
        response?.data?.items ||
        response?.items ||
        [];
      const activeBadges = Array.isArray(items)
        ? items.filter((item) => item?.status === "active" && item?.currentSupportDisplayName)
        : [];

      setSupportBadges((previous) => ({
        ...previous,
        [orgId]: activeBadges,
      }));
    });
  }, [dataMap, supportBadgeRequestMap]);

  const renderSupportBadges = (orgId) =>
    (supportBadges[orgId] || []).map((badge) => (
      <SupportBadge key={badge.supportTokenId || badge.currentSupportDisplayName} badge={badge} />
    ));

  const table = useTableInstance({
    tableName: "hierarchyRealmOrg",
    data: orgData,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  const emptyHead = useTableInstance({
    tableName: "emptyRealmOrg",
    data: '',
    columns: columnsData,
    enableSorting: true,
  });

  return (
    <>
      {!isSurveillance && !scopeReady ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div className={`main-card-content`}
          style={{
            marginTop: '60px', flex: (orgData.length > 0) ? '' : '1',
            display: 'flex', flexDirection: 'column'
          }}>
          <div>
            <Grid container style={{ justifyContent: 'space-between', marginBottom: '10px' }}>
              <Box style={{ display: 'flex' }}>
                <Grid container sx={{ alignItems: "center", }}>
                  <Typography fontSize="16px" fontWeight="600" color="rgba(0, 0, 0, 0.8)">
                    Organizations
                  </Typography>
                </Grid>
              </Box>
              <Box style={{ display: 'flex' }}>
                {orgData.length > 0 && <Grid container sx={{ alignItems: "center", gap: '15px' }}>
                  <SearchInput
                    value={table.globalFilter}
                    onChange={table.setGlobalFilter}
                  />
                  {enableAction && renderAddOrganizationMenu()}
                </Grid>}
              </Box>
            </Grid>
          </div>
          {orgData.length > 0 &&
            <TableField
              {...table}
            />
          }
          {orgData.length === 0 && <>
            <TableField
              {...emptyHead}
            />
            <div style={{ flex: '1', alignContent: 'center', justifyItems: 'center' }}>
              <Grid container style={{ justifyContent: 'center', marginBottom: '40px' }}>
                <Typography sx={{ textAlign: 'center' }}
                  fontSize="16px" fontWeight="600" color="rgba(0, 0, 0, 0.8)">
                  No entry in the table, you can create a new organization.
                </Typography>
              </Grid>
              <Box>
                {enableAction && renderAddOrganizationMenu()}
              </Box>
            </div>
          </>}
        </div>
      )}
      <ModalContainer
        display={openModalCheck}
        modalWidth={'500px'} //550px, 750px, empty=1000px,
        children={
          <ModalforCheck
            title={modalCheckTitle}
            content={modalCheckContent}
            cancelBtn={modalCheckCancelBtn}
            saveBtn={modalCheckSaveBtn}
            setOpenModal={setOpenModalCheck}
            setModalAction={() => { }}
          />
        }
      />
      <ModalContainer display={openCloneOrgSource} modalWidth={cloneOrgPage === "cloning" ? "600px" : "680px"}>
        <Box
          sx={{
            width: "100%",
            bgcolor: "#FFFFFF",
            maxHeight: "90vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "20px",
              py: "14px",
              borderBottom: "1px solid #D9D9D9",
              mx: "-20px",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: "#172D6B", fontSize: "16px", fontWeight: 700 }}>
              {cloneOrgPage === "success" ? "Organization Created" : "Clone Organization"}
            </Typography>
            {cloneOrgPage !== "cloning" && (
              <IconButton
                aria-label="Close Clone Organization"
                onClick={cloneOrgPage === "success" ? handleCloseCloneOrgSuccess : () => setOpenCloneOrgSource(false)}
                size="small"
              >
                <CloseIcon sx={{ fontSize: "20px" }} />
              </IconButton>
            )}
          </Box>
          {!['cloning', 'success'].includes(cloneOrgPage) && (
            <Box sx={{ display: "flex", alignItems: "center", px: "20px", pt: "18px", pb: "16px", flexShrink: 0 }}>
              {["Source Organization", "Organization Details", "Review", "Cloning"].map((label, index) => {
                const stepNumber = index + 1;
                const activeStep = cloneOrgPage === "source" ? 1 : cloneOrgPage === "edit" ? 2 : 3;
                const completed = activeStep > stepNumber;
                const active = activeStep === stepNumber;

                return (
                  <React.Fragment key={label}>
                    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          bgcolor: completed ? "#14A83B" : active ? "#172D6B" : "#ECEEF3",
                          color: active || completed ? "#FFFFFF" : "#A2A8B3",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        {completed ? "✓" : stepNumber}
                      </Box>
                      <Typography
                        sx={{
                          ml: "8px",
                          color: active ? "#172D6B" : completed ? "#667085" : "#858D9D",
                          fontSize: "14px",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    {index < 3 && (
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: "12px",
                          mx: "12px",
                          height: "2px",
                          bgcolor: completed ? "#53B36B" : "#E2E5EC",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          )}
          <Box
            className={selectStyle["custom-scrollbar-menu"]}
            aria-busy={clonePlanLoading}
            sx={{ px: "20px", pt: "18px", pb: "20px", flex: 1, minHeight: 0, overflowY: "auto" }}
          >
            {clonePlanLoading ? (
              <Box
                role="status"
                sx={{ minHeight: "210px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <CircularProgress sx={{ color: "primary.mainHover" }} />
              </Box>
            ) : <>
              {cloneOrgPage === "cloning" && (
                <Box sx={{ minHeight: "210px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", color: "#444444" }}>
                  <CircularProgress sx={{ color: "primary.mainHover", mb: "16px" }} />
                  <Typography sx={{ fontSize: "16px", fontWeight: 700, mb: "8px" }}>Organization Cloning...</Typography>
                  <Typography sx={{ fontSize: "13px" }}>Creating “{cloneOrgForm.name}” and applying cloned configurations</Typography>
                </Box>
              )}

              {cloneOrgPage === "success" && (
                <Box sx={{ minHeight: "440px", px: "28px", pt: "55px", pb: "45px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", color: "#444444" }}>
                  <Box sx={{ width: "58px", height: "58px", borderRadius: "50%", bgcolor: "#E7F7EB", display: "flex", alignItems: "center", justifyContent: "center", mb: "18px" }}>
                    <Typography sx={{ color: "#13A33A", fontSize: "34px", fontWeight: 500, lineHeight: 1 }}>✓</Typography>
                  </Box>
                  <Typography sx={{ color: "#172D6B", fontSize: "19px", fontWeight: 700, textAlign: "center", mb: "8px" }}>
                    “{cloneOrgResult?.orgName || cloneOrgForm.name}” is ready!
                  </Typography>
                  <Typography sx={{ color: "#858D9D", fontSize: "13px", lineHeight: "19px", textAlign: "center", mb: "16px" }}>
                    The new organization and {cloneOrgResult?.siteCount ?? 0} site(s) are listed under {bread?.realm?.name || "this MSP"}.
                  </Typography>
                  <SolidButton height="32px" onClick={() => handleOpenClonedOrganization("devices")}>
                    Add New Devices
                  </SolidButton>
                </Box>
              )}

              {cloneOrgPage === "source" && (
                <>
                  <Typography sx={{ fontSize: "13px", lineHeight: "18px", color: "#7D8492", mb: "14px" }}>
                    Choose the organization whose configuration you want to copy. Every site in it is cloned, with its own settings.
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "220px minmax(0, 1fr)",
                      alignItems: "start",
                      rowGap: "24px",
                      width: "100%",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: "primary.greyPrimary", pt: "8px" }}>Target MSP</Typography>
                    <Box>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#444444" }}>
                        {bread?.realm?.name || "Current MSP"}
                      </Typography>
                      <Typography sx={{ mt: "4px", fontSize: "13px", color: "#888888" }}>
                        You are currently in this MSP, so the new organization will be created here.
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "14px", color: "primary.greyPrimary", pt: "8px" }}>Source Organization</Typography>
                    <Box>
                      <MenuSelect
                        value={cloneSourceOrgId}
                        onChange={(option) => handleCloneSourceChange(option?.value || "")}
                        options={orgData.map((organization) => ({ value: organization.id, label: organization.name }))}
                        width="100%"
                        controlHeight="36px"
                        disabled={clonePlanLoading}
                        placeholder="Select an organization to clone"
                      />
                      <Typography sx={{ mt: "5px", fontSize: "13px", color: "#888888" }}>
                        Lists only organizations that already sit in the target MSP above.
                      </Typography>
                      {clonePlanError && (
                        <Box sx={{ mt: "8px" }}>
                          <Typography role="alert" sx={{ fontSize: "13px", color: "#B42318" }}>{clonePlanError}</Typography>
                          <OutlineButton height="30px" onClick={loadClonePlanFromSource} sx={{ mt: "6px" }}>Retry</OutlineButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              {cloneOrgPage === "edit" && (
                <>
                  <Typography sx={{ fontSize: "13px", lineHeight: "18px", color: "#7D8492", mb: "14px" }}>
                    Choose the organization whose configuration you want to copy. Every site in it is cloned, with its own settings.
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "220px minmax(0, 1fr)",
                      alignItems: "start",
                      rowGap: "20px",
                      width: "100%",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", color: "primary.greyPrimary", pt: "8px" }}>Target MSP</Typography>
                    <Box>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#444444" }}>
                        {bread?.realm?.name || "Current MSP"}
                      </Typography>
                      <Typography sx={{ mt: "4px", fontSize: "13px", color: "#888888" }}>
                        You are currently in this MSP, so the new organization will be created here.
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "14px", color: "primary.greyPrimary", pt: "8px" }}>Source Organization</Typography>
                    <Box>
                      <MenuSelect
                        value={cloneSourceOrgId}
                        onChange={(option) => handleCloneSourceChange(option?.value || "")}
                        options={orgData.map((organization) => ({ value: organization.id, label: organization.name }))}
                        width="100%"
                        controlHeight="36px"
                        disabled={clonePlanLoading}
                        placeholder="Select an organization to clone"
                      />
                      <Typography sx={{ mt: "5px", fontSize: "13px", color: "#888888" }}>
                        Lists only organizations that already sit in the target MSP above.
                      </Typography>
                      {clonePlan?.canClone === false && (
                        <Typography role="alert" sx={{ mt: "8px", fontSize: "13px", color: "#B42318" }}>
                          {clonePlan.blockReason === "userTreeNotGrantable"
                            ? "Some source user grants exceed your permissions. The user management tree must be unchecked to continue."
                            : "This organization cannot be cloned with the current selection."}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Typography sx={{ borderTop: "1px solid #D9D9D9", mt: "20px", pt: "20px", mb: "18px", fontSize: "14px", lineHeight: 1.5, color: "#333333" }}>
                    The following settings will apply to all future sites within this organization. Cloned sites will inherit the country, time zone, NTP server, and device account details from their original source sites.
                  </Typography>

                  <Box sx={{ mb: "14px" }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#34456D", mb: "5px" }}>
                      New Organization Name *
                    </Typography>
                    <OrgCloneTextField value={cloneOrgForm.name} onChange={(value) => handleCloneOrgFormChange("name", value)} />
                    <Typography sx={{ mt: "4px", color: "#939AA8", fontSize: "12px", lineHeight: "16px" }}>
                      Default is the source organization name + " - Copy".
                    </Typography>
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", mb: "14px" }}>
                    <Box>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#34456D", mb: "5px" }}>Country *</Typography>
                      <AutocompleteSelect
                        value={cloneOrgCountryOptions.find((country) => String(country.value) === String(cloneOrgForm.countryIndex)) || null}
                        setValue={handleCloneOrgCountryChange}
                        options={cloneOrgCountryOptions}
                        width="100%"
                        placeholder="Select a Country"
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#34456D", mb: "5px" }}>Time Zone *</Typography>
                      <MenuSelect
                        value={cloneOrgForm.timeZone}
                        onChange={(option) => handleCloneOrgFormChange("timeZone", option?.value || "")}
                        options={cloneOrgTimezoneOptions}
                        width="100%"
                        controlHeight="36px"
                        placeholder={cloneOrgForm.countryIndex ? "Select a Time Zone" : "Select a country first"}
                        disabled={!cloneOrgForm.countryIndex}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mb: "14px" }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#34456D", mb: "5px" }}>NTP Servers *</Typography>
                    {cloneOrgForm.ntpServers.map((server, index) => (
                      <Box key={`clone-ntp-${index}`} sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "7px" }}>
                        <Box sx={{ flex: 1 }}>
                          <OrgCloneTextField value={server} onChange={(value) => handleCloneOrgNtpChange(index, value)} />
                        </Box>
                        {index > 0 && (
                          <IconButton size="small" aria-label={`Remove NTP server ${index + 1}`} onClick={() => removeCloneOrgNtpServer(index)}>
                            <NtpRemoveIcon />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "3px" }}>
                      <SolidButton
                        width="80px"
                        height="30px"
                        startIcon={<AddIcon />}
                        onClick={addCloneOrgNtpServer}
                        disabled={cloneOrgForm.ntpServers.length >= 3}
                      >
                        Add
                      </SolidButton>
                    </Box>
                  </Box>

                  <Box sx={{ mb: "6px" }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#34456D" }}>
                      Inventory Device Account
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", mb: "8px" }}>
                    <Box>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#34456D", mb: "5px" }}>Username *</Typography>
                      <OrgCloneTextField
                        value={cloneOrgForm.username}
                        onChange={(value) => handleCloneOrgFormChange("username", value)}
                        maxLength={32}
                        isValid={!cloneOrgUsernameError}
                        errorHelperText="Invalid username."
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "5px" }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#34456D" }}>Password *</Typography>
                        <Tooltip
                          title={passwordRequirementsTip}
                          arrow
                          placement="top"
                          slotProps={{ tooltip: { sx: { maxWidth: 360, p: "12px 14px", bgcolor: "#5f5f5f" } } }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: 16,
                              ml: "5px",
                              "& img": { content: `url(${infoTipIcon})` },
                              "&:hover img": { content: `url(${infoTipHoverIcon})` },
                            }}
                          >
                            <img src={infoTipIcon} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
                          </Box>
                        </Tooltip>
                      </Box>
                      <OrgCloneTextField
                        value={cloneOrgForm.password}
                        onChange={(value) => handleCloneOrgFormChange("password", value)}
                        type="password"
                        maxLength={30}
                        isValid={!cloneOrgPasswordError}
                        errorHelperText={cloneOrgPasswordError}
                      />
                    </Box>
                  </Box>
                </>
              )}

              {cloneOrgPage === "summary" && (
                <Box sx={{ color: "#444444" }}>
                  <Typography sx={{ color: "#444444", fontSize: "14px", lineHeight: "20px", fontWeight: 700, mb: "14px" }}>
                    Please confirm the following summary
                  </Typography>
                  {cloneOrgNeedsVerification && (
                    <Box sx={{ mb: "12px", p: "10px", border: "1px solid #F2C8A5", borderRadius: "4px", bgcolor: "#FFF8F2" }}>
                      <Typography role="status" sx={{ color: "#8A4B08", fontSize: "12px", lineHeight: "18px" }}>
                        The create result is unknown. Verify the MSP Organization list before retrying.
                      </Typography>
                      {cloneOrgVerificationFailed && (
                        <OutlineButton height="30px" onClick={verifyCloneOutcome} sx={{ mt: "7px" }}>
                          Check Organization List Again
                        </OutlineButton>
                      )}
                    </Box>
                  )}
                  <Box sx={{ border: "1px solid #DDE1EA", borderRadius: "6px", bgcolor: "#FBFCFE", px: "16px", py: "14px" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "200px minmax(0, 1fr)", rowGap: "5px", columnGap: "10px" }}>
                      {[
                        ["Creating New Organization:", cloneOrgForm.name],
                        ["Cloned From:", cloneOrgSourceName],
                        ["Target:", bread?.realm?.name ? `${bread.realm.name} (MSP)` : "Current MSP"],
                        ["Time Zone:", cloneOrgTimezoneName],
                        ["Country / Region:", cloneOrgCountryName],
                      ].map(([label, value]) => (
                        <React.Fragment key={label}>
                          <Typography sx={{ color: "#7B8495", fontSize: "12px", lineHeight: "17px" }}>{label}</Typography>
                          <Typography sx={{ color: "#172D6B", fontSize: "12px", lineHeight: "17px", fontWeight: 700, wordBreak: "break-word" }}>{value || "-"}</Typography>
                        </React.Fragment>
                      ))}
                      {cloneOrgForm.ntpServers.filter(Boolean).map((server, index) => (
                        <React.Fragment key={`summary-ntp-${index}`}>
                          <Typography sx={{ color: "#7B8495", fontSize: "12px", lineHeight: "17px" }}>NTP Server {index + 1}:</Typography>
                          <Typography sx={{ color: "#172D6B", fontSize: "12px", lineHeight: "17px", fontWeight: 700, wordBreak: "break-word" }}>{server}</Typography>
                        </React.Fragment>
                      ))}
                      <Typography sx={{ color: "#7B8495", fontSize: "12px", lineHeight: "17px" }}>Device Account:</Typography>
                      <Typography sx={{ color: "#172D6B", fontSize: "12px", lineHeight: "17px", fontWeight: 700, wordBreak: "break-word" }}>{cloneOrgForm.username || "-"}</Typography>
                      <Typography sx={{ color: "#7B8495", fontSize: "12px", lineHeight: "17px" }}>Device Password:</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <Typography sx={{ color: "#172D6B", fontSize: "12px", lineHeight: "17px", fontWeight: 700, letterSpacing: showCloneOrgPassword ? "normal" : "2px" }}>
                          {showCloneOrgPassword ? cloneOrgForm.password : "••••••••"}
                        </Typography>
                        <IconButton size="small" onClick={() => setShowCloneOrgPassword((previous) => !previous)} sx={{ p: "2px" }}>
                          {showCloneOrgPassword ? <HideIcon sx={{ fontSize: "14px" }} /> : <ShowIcon sx={{ fontSize: "14px" }} />}
                        </IconButton>
                      </Box>
                    </Box>

                    <Box sx={{ height: "1px", bgcolor: "#E5E7EB", my: "16px" }} />
                    {(clonePlan?.sites || []).map((site, index) => {
                      const siteKey = site.sourceSiteId || index;
                      const summary = site.summary || {};
                      const expanded = Boolean(expandedCloneOrgSites[siteKey]);
                      const sections = [
                        ["General Settings", 1],
                        ["Wireless Networks", summary.wirelessNetworkNames?.length, summary.wirelessNetworkNames],
                        ["Wireless Radio Settings", 1],
                        ["Wired Basic Config", 1],
                        ["Config Provisioning Templates", summary.configProvisioningTemplateLabels?.length, summary.configProvisioningTemplateLabels],
                        ["Captive Portal Profiles", summary.captivePortalProfileCount],
                        ["Port Profiles", summary.portProfileCount],
                        ["MAC Address Groups", summary.macAddressGroupCount],
                        ["RADIUS Server Profiles", summary.radiusServerProfileCount],
                        ["Schedule Profiles", summary.scheduleProfileCount],
                        ["IP Address Profiles", summary.ipAddressProfileCount],
                        ["User Account Profiles", summary.userAccountProfileCount],
                        ["LDAP Server Profiles", summary.ldapServerProfileCount],
                        ["Voice VLAN", summary.voiceVlanEnabled ? 1 : 0],
                        ["QoS", summary.qosModified ? 1 : 0],
                        ["IPv4 ACL Rules", summary.ipv4AclRuleCount],
                        ["Model Scheduled Reboot Rules", summary.modelScheduledRebootRuleCount],
                        ["Excluded Device Scheduled Reboot Rules", summary.excludedDeviceScheduledRebootRuleCount],
                        ["Site Paid Services", summary.clonedSubscriptionNames?.length, summary.clonedSubscriptionNames],
                      ].filter(([, count]) => count > 0);
                      return (
                        <Box key={siteKey} sx={{ border: "1px solid #DDE1EA", borderRadius: "4px", bgcolor: "#FFFFFF", mb: "8px" }}>
                          <Box
                            component="button"
                            type="button"
                            aria-expanded={expanded}
                            onClick={() => setExpandedCloneOrgSites((previous) => ({ ...previous, [siteKey]: !expanded }))}
                            sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left", border: 0, bgcolor: "transparent", px: "12px", py: "10px", color: "inherit", font: "inherit", cursor: "pointer" }}
                          >
                            <Box component="span" sx={{ color: "#4D5664", fontSize: "12px" }}>
                              <Box component="strong">{site.siteName || "Site"}</Box>
                              {` · ${cloneOrgCountryOptions.find((country) => String(country.value) === String(site.countryIndex))?.label || `Country ${site.countryIndex ?? "—"}`} / ${summary.wirelessNetworkNames?.length || 0} Wireless / ${summary.captivePortalProfileCount || 0} Portal`}
                            </Box>
                            <ExpandMoreIcon
                              sx={{
                                color: "#172D6B",
                                transform: expanded ? "rotate(180deg)" : "none",
                                transition: "transform 150ms ease",
                              }}
                            />
                          </Box>
                          {expanded && (
                            <Box sx={{ borderTop: "1px solid #E5E7EB", px: "12px", py: "10px" }}>
                              {sections.map(([name, count, names]) => (
                                <Box key={name} sx={{ display: "flex", alignItems: "flex-start", gap: "7px", mb: "5px" }}>
                                  <Typography sx={{ color: "#14A83B", fontSize: "14px", fontWeight: 700, lineHeight: "18px" }}>✓</Typography>
                                  <Typography sx={{ color: "#4D5664", fontSize: "12px", lineHeight: "18px" }}>{count} {name}{names?.length ? ` (${names.join(", ")})` : ""}</Typography>
                                </Box>
                              ))}
                              {summary.administratorNames?.length > 0 && (
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: "7px" }}>
                                  <Typography sx={{ color: "#14A83B", fontSize: "14px", fontWeight: 700, lineHeight: "18px" }}>✓</Typography>
                                  <Typography sx={{ color: "#4D5664", fontSize: "12px", lineHeight: "18px" }}>{summary.administratorNames.length} administrator(s) ({summary.administratorNames.join(", ")}) will have access; notification settings are retained</Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </>}
          </Box>
          {cloneOrgPage !== "cloning" && (
            <Box
              sx={{
                px: "20px",
                py: "12px",
                borderTop: "1px solid #E5E7EB",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                bgcolor: "#FFFFFF",
              }}
            >
              {cloneOrgPage === "success" ? (
                <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <OutlineButton height="34px" onClick={handleCloseCloneOrgSuccess} sx={{ whiteSpace: "nowrap" }}>
                    Back to MSP List
                  </OutlineButton>
                  <SolidButton height="34px" onClick={() => handleOpenClonedOrganization("sites")} sx={{ whiteSpace: "nowrap" }}>
                    View New Organization
                  </SolidButton>
                </Box>
              ) : <>
                <OutlineButton
                  height="34px"
                  width="84px"
                  onClick={() => {
                    if (cloneOrgPage === "summary") setCloneOrgPage("edit");
                    else if (cloneOrgPage === "edit") setCloneOrgPage("source");
                    else setOpenCloneOrgSource(false);
                  }}
                >
                  {cloneOrgPage === "source" ? "Cancel" : "Back"}
                </OutlineButton>
                <SolidButton
                  height="34px"
                  width={cloneOrgPage === "summary" ? "160px" : "84px"}
                  disabled={cloneOrgLoading || cloneOrgNeedsVerification || (cloneOrgPage === "source"
                    ? !cloneSourceOrgId || clonePlanLoading
                    : cloneOrgPage === "edit"
                      ? !cloneOrgFormValid || clonePlanLoading || !clonePlan || !clonePlan.canClone
                      : false)}
                  onClick={() => {
                    if (cloneOrgPage === "source") loadClonePlanFromSource();
                    else if (cloneOrgPage === "edit") {
                      setCloneOrgPage("summary");
                      setExpandedCloneOrgSites({});
                    } else if (cloneOrgPage === "summary") handleCloneOrganization();
                  }}
                >
                  {cloneOrgPage === "summary" ? (cloneOrgLoading ? "Creating..." : "Clone Organization") : "Next"}
                </SolidButton>
              </>}
            </Box>
          )}
        </Box>
      </ModalContainer>
      <ModalForAddOrgOrSite
        auth="Organization"
        display={openModalForAddOrg}
        title={modalForAddOrgTitle}
        setOpenModal={setOpenModalForAddOrg}
        cancelBtn={modalForAddOrgCancelBtn}
        saveBtn={modalForAddOrgSaveBtn}
        errorHandling={handleActionDenied}
        refresh={getDatas}
      />
      <GlobalAlert
        open={cloneOrgAlert.open}
        severity={cloneOrgAlert.severity}
        message={cloneOrgAlert.message}
        onClose={() => setCloneOrgAlert((previous) => ({ ...previous, open: false }))}
      />
    </>
  );
};

export default Organization;
